/**
 * Sandbox · nueva corrida.
 *
 * Sube un CSV/JSON con preguntas, las parsea client-side, y dispara una run
 * contra /api/sandbox con la versión productiva del manual del cliente.
 *
 * El wizard original (v2.1) tenía "diff mode" baseline vs candidate como mock —
 * el backend todavía no soporta diff por run. Se removió hasta que exista
 * `compare_against_version_id` en cm_sandbox_runs. Por ahora una run = una
 * versión del manual.
 */
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Check, ChevronRight, Play, Upload, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useSkillsForClient } from "@/hooks/use-skills";
import {
  useCreateSandboxRun,
  useSandboxRuns,
} from "@/hooks/use-sandbox-run";
import { findClient } from "@/lib/clients";
import type { ClientSlug, SandboxRun } from "@/types";

interface ParsedQuestion {
  id: string;
  text: string;
}

function parseCsv(text: string): ParsedQuestion[] {
  // Mini parser CSV: una columna llamada question/pregunta/text/q en header,
  // o sin header tratamos cada línea como pregunta. Soporta comillas dobles
  // y comas en valor. No es RFC-4180 estricto pero cubre 99% de casos reales.
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const header = lines[0]!.toLowerCase();
  const headerCols = header.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const questionIdx = headerCols.findIndex((c) =>
    ["question", "pregunta", "text", "q", "msg", "message"].includes(c),
  );
  const hasHeader = questionIdx >= 0;
  const data = hasHeader ? lines.slice(1) : lines;
  const out: ParsedQuestion[] = [];
  for (let i = 0; i < data.length; i++) {
    const line = data[i]!;
    let value = line;
    if (hasHeader) {
      // split simple: respeta comillas pareadas, no escape interno.
      const cells: string[] = [];
      let cur = "";
      let inQuote = false;
      for (const ch of line) {
        if (ch === '"') inQuote = !inQuote;
        else if (ch === "," && !inQuote) {
          cells.push(cur);
          cur = "";
        } else cur += ch;
      }
      cells.push(cur);
      value = (cells[questionIdx] ?? "").trim().replace(/^"|"$/g, "");
    }
    if (!value) continue;
    out.push({ id: `q${i + 1}`, text: value });
  }
  return out;
}

function parseJson(text: string): ParsedQuestion[] {
  const data = JSON.parse(text);
  if (!Array.isArray(data)) {
    throw new Error("El JSON debe ser un array de strings o de {question} / {text}.");
  }
  return data
    .map((item, i): ParsedQuestion | null => {
      if (typeof item === "string") return { id: `q${i + 1}`, text: item };
      if (item && typeof item === "object") {
        const t = item.question ?? item.text ?? item.q ?? item.body;
        if (typeof t === "string") return { id: item.id ?? `q${i + 1}`, text: t };
      }
      return null;
    })
    .filter((q): q is ParsedQuestion => q !== null);
}

export function SandboxNewPage() {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { data: recentRuns } = useSandboxRuns();
  const createRun = useCreateSandboxRun();

  const availableClients = useMemo(() => {
    const slugs = user?.clientAccess ?? [];
    return slugs
      .map((s) => findClient(s))
      .filter((c): c is NonNullable<ReturnType<typeof findClient>> => !!c);
  }, [user]);

  const [clientSlug, setClientSlug] = useState<ClientSlug | "">("");
  const { data: skills } = useSkillsForClient(
    clientSlug ? (clientSlug as ClientSlug) : undefined,
  );
  const [skillId, setSkillId] = useState<string>("");
  const selectedSkill = skills?.find((s) => s.id === skillId) ?? null;

  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedQuestion[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  async function handleFile(f: File | null) {
    setParseError(null);
    setParsed([]);
    setFile(f);
    if (!f) return;
    try {
      const text = await f.text();
      const isJson = f.name.toLowerCase().endsWith(".json");
      const rows = isJson ? parseJson(text) : parseCsv(text);
      if (rows.length === 0) throw new Error("No se detectaron preguntas en el archivo.");
      if (rows.length > 1000) throw new Error(`Demasiadas preguntas (${rows.length}). Máximo 1000 por run.`);
      setParsed(rows);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
      setFile(null);
    }
  }

  const platform = "instagram"; // platform por ahora fijo — la decisión por canal
  // la hace el manual, no la run en sí. A futuro un dropdown si los manuales
  // separan IG/FB/WA explícitamente.

  const canRun =
    !!clientSlug &&
    !!selectedSkill?.productionVersionId &&
    parsed.length > 0 &&
    !createRun.isPending;

  async function handleRun() {
    if (!canRun || !clientSlug || !selectedSkill?.productionVersionId) return;
    const res = await createRun.mutateAsync({
      clientSlug: clientSlug as ClientSlug,
      platform,
      skillVersion: selectedSkill.productionVersionId,
      questions: parsed,
    });
    navigate({ to: "/sandbox/$runId", params: { runId: res.runId } });
  }

  const steps = useMemo(() => {
    const s1 = !!clientSlug;
    const s2 = !!skillId && !!selectedSkill?.productionVersionId;
    const s3 = parsed.length > 0;
    const states: Array<"complete" | "current" | "pending"> = [
      s1 ? "complete" : "current",
      s1 ? (s2 ? "complete" : "current") : "pending",
      s1 && s2 ? (s3 ? "complete" : "current") : "pending",
    ];
    return states;
  }, [clientSlug, skillId, selectedSkill, parsed.length]);

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col min-w-0">
        <header className="px-8 py-5 border-b border-subtle bg-surface-1">
          <div className="flex items-center gap-1.5 text-xs text-text-muted">
            <span>Sandbox</span>
            <ChevronRight size={11} className="text-text-disabled" />
            <span className="text-text-secondary">Nueva corrida</span>
          </div>
          <h1 className="font-sans font-semibold text-2xl tracking-snug text-text-primary mt-0.5">
            Nueva corrida
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Probá el manual del cliente contra un lote de preguntas. Sin envío a la red.
          </p>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-8 py-8">
            <Stepper states={steps} labels={["Cliente", "Manual", "Preguntas"]} />

            {/* 1 — Cliente */}
            <StepCard step={1} status={steps[0]} title="Cliente">
              <select
                value={clientSlug}
                onChange={(e) => {
                  setClientSlug(e.target.value as ClientSlug);
                  setSkillId("");
                }}
                className="h-10 w-full rounded-md px-3 bg-surface-2 border border-default text-sm text-text-primary focus:outline-none focus:border-magenta"
              >
                <option value="">— elegí cliente —</option>
                {availableClients.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.fullName}
                  </option>
                ))}
              </select>
            </StepCard>

            {/* 2 — Manual */}
            <StepCard step={2} status={steps[1]} title="Manual del cliente">
              {!clientSlug ? (
                <p className="text-sm text-text-muted">Elegí un cliente primero.</p>
              ) : !skills?.length ? (
                <p className="text-sm text-text-muted">
                  Este cliente no tiene un manual creado. Creá uno desde /skills.
                </p>
              ) : (
                <>
                  <select
                    value={skillId}
                    onChange={(e) => setSkillId(e.target.value)}
                    className="h-10 w-full rounded-md px-3 bg-surface-2 border border-default text-sm text-text-primary focus:outline-none focus:border-magenta"
                  >
                    <option value="">— elegí manual —</option>
                    {skills.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {selectedSkill && !selectedSkill.productionVersionId && (
                    <p className="text-2xs text-warning-text mt-2">
                      Este manual no tiene versión productiva. Promové una en /skills antes de correr.
                    </p>
                  )}
                </>
              )}
            </StepCard>

            {/* 3 — Preguntas */}
            <StepCard step={3} status={steps[2]} title="Preguntas">
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFile(e.dataTransfer.files?.[0] ?? null);
                }}
                className={cn(
                  "block rounded-xl border border-dashed px-6 py-8 text-center cursor-pointer transition-colors",
                  dragOver
                    ? "border-magenta bg-magenta/5"
                    : "border-default hover:border-magenta",
                )}
              >
                <div className="flex justify-center mb-3">
                  <div className="w-11 h-11 rounded-xl bg-surface-2 border border-default flex items-center justify-center text-text-secondary">
                    <Upload size={20} strokeWidth={1.75} />
                  </div>
                </div>
                <div className="text-sm font-medium text-text-primary mb-1">
                  Arrastrá un .csv o .json
                </div>
                <div className="text-2xs text-text-muted">
                  o <span className="underline text-magenta">elegí desde el equipo</span>
                </div>
                <input
                  type="file"
                  accept=".csv,.json,text/csv,application/json"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
              </label>

              <p className="text-2xs text-text-muted mt-2">
                CSV: una columna llamada <code className="font-mono">question</code> /{" "}
                <code className="font-mono">pregunta</code> /{" "}
                <code className="font-mono">text</code>. Sin header: cada línea es una pregunta.{" "}
                JSON: array de strings o de objetos con campo <code className="font-mono">question</code>.
              </p>

              {parseError && (
                <div className="mt-3 rounded-md border border-error-border bg-error-bg px-3 py-2 text-sm text-error-text">
                  {parseError}
                </div>
              )}

              {file && parsed.length > 0 && (
                <div className="mt-3 flex items-center gap-3 px-4 py-3 rounded-md bg-surface-2 border border-subtle">
                  <div className="w-8 h-8 rounded-md bg-success-bg border border-success-border text-success-text inline-flex items-center justify-center">
                    <Check size={14} strokeWidth={2.25} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-medium text-text-primary truncate">
                      {file.name}
                    </div>
                    <div className="text-2xs text-text-muted mt-0.5">
                      {parsed.length} preguntas detectadas
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setParsed([]);
                    }}
                    className="w-8 h-8 rounded-md text-text-secondary hover:bg-surface-3 hover:text-text-primary inline-flex items-center justify-center"
                  >
                    <X size={14} strokeWidth={1.75} />
                  </button>
                </div>
              )}

              {parsed.length > 0 && (
                <div className="mt-5">
                  <div className="text-2xs uppercase tracking-wider text-text-muted mb-2">
                    Preview · primeras 5 de {parsed.length}
                  </div>
                  <div className="rounded-md overflow-hidden border border-subtle">
                    <table className="w-full text-sm">
                      <tbody>
                        {parsed.slice(0, 5).map((q, i) => (
                          <tr
                            key={q.id}
                            className={i < 4 ? "border-b border-subtle" : ""}
                          >
                            <td className="px-3 py-2.5 font-mono tabular-nums text-text-muted w-10">
                              {String(i + 1).padStart(2, "0")}
                            </td>
                            <td className="px-3 py-2.5 text-text-primary">
                              {q.text}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </StepCard>

            <div className="sticky bottom-0 -mx-8 mt-8 px-8 py-3.5 bg-background border-t border-subtle flex items-center justify-between gap-4">
              <div className="text-2xs font-mono tabular-nums text-text-muted">
                {parsed.length > 0 ? `${parsed.length} preguntas listas` : "—"}
              </div>
              <button
                onClick={handleRun}
                disabled={!canRun}
                className={cn(
                  "inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-magenta text-white text-sm font-medium",
                  "hover:bg-magenta-hover transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <Play size={12} strokeWidth={2.25} className="fill-current" />
                {createRun.isPending ? "Lanzando…" : "Run sandbox"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <aside className="w-80 border-l border-subtle bg-surface-1 flex flex-col flex-shrink-0">
        <div className="h-16 px-5 flex items-center border-b border-subtle">
          <h3 className="font-sans font-semibold text-base text-text-primary">Recent runs</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {recentRuns?.slice(0, 8).map((r) => <RecentCard key={r.id} run={r} />)}
          {!recentRuns?.length && (
            <p className="text-2xs text-text-muted px-2">Sin corridas previas.</p>
          )}
        </div>
      </aside>
    </div>
  );
}

function Stepper({
  states,
  labels,
}: {
  states: Array<"complete" | "current" | "pending">;
  labels: string[];
}) {
  return (
    <div className="flex items-center w-full mb-10">
      {labels.map((label, i) => {
        const state = states[i];
        const isLast = i === labels.length - 1;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <div
                className={cn(
                  "w-7 h-7 rounded-full inline-flex items-center justify-center text-sm font-medium border",
                  state === "current" && "bg-magenta border-magenta text-white",
                  state === "complete" && "bg-success-bg border-success-border text-success-text",
                  state === "pending" && "bg-surface-1 border-default text-text-muted",
                )}
              >
                {state === "complete" ? <Check size={13} strokeWidth={2.5} /> : i + 1}
              </div>
              <span
                className={cn(
                  "text-sm font-medium",
                  state === "current" && "text-text-primary",
                  state === "complete" && "text-text-secondary",
                  state === "pending" && "text-text-muted",
                )}
              >
                {label}
              </span>
            </div>
            {!isLast && (
              <div
                className={cn(
                  "flex-1 h-px mx-3.5",
                  state === "complete" ? "bg-success-border" : "bg-subtle",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepCard({
  step,
  status,
  title,
  children,
}: {
  step: number;
  status: "complete" | "current" | "pending";
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border bg-surface-1 px-5 py-5 mb-5",
        status === "current" ? "border-magenta shadow-sm" : "border-default",
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className={cn(
            "text-2xs",
            status === "current" ? "text-magenta" : "text-text-muted",
          )}
        >
          Paso {step}
        </span>
        {status === "complete" && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-success-border bg-success-bg text-success-text text-2xs">
            <Check size={9} strokeWidth={3} />
            OK
          </span>
        )}
      </div>
      <h2 className="font-sans text-base font-medium tracking-snug text-text-primary mb-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

function RecentCard({ run }: { run: SandboxRun }) {
  const progress = Math.round(
    (run.processed / Math.max(run.totalQuestions, 1)) * 100,
  );
  return (
    <article className="rounded-xl border border-default bg-surface-1 p-3.5 hover:border-strong transition-colors">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <span className="font-mono text-xs font-medium text-text-primary truncate">
          {run.skillVersion.slice(0, 8)}…
        </span>
        <span
          className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-full border text-2xs font-medium",
            run.status === "done" &&
              "bg-success-bg border-success-border text-success-text",
            run.status === "running" && "bg-info-bg border-info-border text-info-text",
            run.status === "failed" && "bg-error-bg border-error-border text-error-text",
            run.status === "queued" && "bg-surface-2 border-default text-text-muted",
          )}
        >
          {run.status}
        </span>
      </div>
      <div className="text-2xs font-mono tabular-nums text-text-muted">
        {run.processed} / {run.totalQuestions} preguntas
      </div>
      {run.status === "running" && (
        <div className="h-1 rounded-full bg-surface-3 overflow-hidden mt-2">
          <div
            className="h-1 rounded-full bg-info-text transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </article>
  );
}
